import { AlertTriangle, CheckCircle, Database, Server } from 'lucide-react';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { useSystemHealth } from '../../api/admin';

/** System health — services, queue depth, replica lag, business DB count, recent incidents. */
export function SystemHealth() {
  const { data: systemHealth } = useSystemHealth();
  if (!systemHealth) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Loading system health…
      </div>
    );
  }
  const totalDbs = systemHealth.databaseDistribution.managed + systemHealth.databaseDistribution.selfHost + systemHealth.databaseDistribution.legacy;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="System health"
        subtitle="Live infrastructure status and recent incidents."
        action={
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            All systems nominal
          </span>
        }
      />

      <Card className="p-5 sm:p-6 mb-4">
        <CardHeader title="Services" subtitle={`${systemHealth.services.length} services monitored`} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {systemHealth.services.map((svc) => (
            <div key={svc.name} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
              <span className="w-9 h-9 grid place-items-center rounded-md bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300">
                <Server className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{svc.name}</div>
                <div className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                  <CheckCircle className="w-3 h-3" />
                  Healthy
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-3 sm:gap-4 mb-4">
        <Card className="p-5 sm:p-6">
          <CardHeader title="Queue depth" subtitle="Background jobs waiting to be processed" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(systemHealth.queueDepth).map(([name, n]) => (
              <div key={name} className="rounded-md p-3 bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
                <div className={cn(
                  'text-2xl font-semibold tabular-nums',
                  n === 0 ? 'text-zinc-400 dark:text-zinc-500' : n < 10 ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300',
                )}>
                  {n}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 capitalize">{name}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Replica lag" subtitle="MariaDB read replica" />
          <div className="flex items-end gap-3">
            <div className="text-3xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
              {systemHealth.replicaLagSec.toFixed(1)} s
            </div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400 mb-1.5">
              threshold: {systemHealth.replicaLagThresholdSec} s
            </div>
          </div>
          <div className="mt-4 h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 dark:bg-emerald-400 rounded-full"
              style={{ width: `${(systemHealth.replicaLagSec / systemHealth.replicaLagThresholdSec) * 100}%` }}
            />
          </div>
        </Card>
      </div>

      <Card className="p-5 sm:p-6 mb-4">
        <CardHeader title="Business databases" subtitle={`${totalDbs} total`} />
        <div className="grid grid-cols-3 gap-3">
          <DbStat
            label="Managed"
            n={systemHealth.databaseDistribution.managed}
            tone="emerald"
            description="Provisioned by us, isolated per business"
          />
          <DbStat
            label="Self-hosted"
            n={systemHealth.databaseDistribution.selfHost}
            tone="info"
            description="Customer-managed, accessed via TLS"
          />
          <DbStat
            label="Legacy (shared)"
            n={systemHealth.databaseDistribution.legacy}
            tone="amber"
            description="Grandfathered into ogrogoti_master"
          />
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Recent incidents" subtitle="Last 7 days" />
        {systemHealth.recentIncidents.length === 0 ? (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No incidents in the last 7 days.</p>
        ) : (
          <ul className="space-y-3">
            {systemHealth.recentIncidents.map((inc) => (
              <li key={inc.id} className="flex items-start gap-3 p-3 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                <span className="w-7 h-7 grid place-items-center rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 flex-shrink-0">
                  <AlertTriangle className="w-3.5 h-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{inc.summary}</span>
                    <Badge tone={inc.resolution === 'resolved' ? 'success' : 'warning'}>
                      {inc.resolution === 'resolved' ? 'Resolved' : 'Acknowledged'}
                    </Badge>
                  </div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 tabular-nums">{inc.at}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function DbStat({
  label,
  n,
  tone,
  description,
}: {
  label: string;
  n: number;
  tone: 'emerald' | 'info' | 'amber';
  description: string;
}) {
  const cls = {
    emerald: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900',
    info: 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900',
    amber: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900',
  }[tone];
  return (
    <div className={cn('rounded-lg p-4 border', cls)}>
      <div className="flex items-center gap-2 mb-2">
        <Database className="w-3.5 h-3.5 opacity-80" />
        <span className="text-xs uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-semibold tabular-nums">{n}</div>
      <div className="text-xs mt-1 opacity-80">{description}</div>
    </div>
  );
}
